import React, { useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    TextField,
    Tooltip,
    InputAdornment,
} from "@mui/material";
import { CopyAll, Close, Visibility, VisibilityOff } from "@mui/icons-material";
import successToast from "../../utils/successToast";
import AutoGeneratePassword from "../../components/autoGeneratePassword";
import { SecondaryButton } from "../../components/buttons/buttons";

interface PasswordSectionProps {
    name?: string;
    control: any;
    setValue: any;
    clearErrors: any;
    errors: any;
    watch: any;
    pathAction: string;
    hospitalDetailsData?: any;
    required?: boolean;
    disabled?: boolean;
    addHospitalName?: any;
}

const PasswordSection: React.FC<PasswordSectionProps> = ({
    name = "userPassword",
    control,
    setValue,
    clearErrors,
    errors,
    watch,
    pathAction,
    hospitalDetailsData,
    disabled = false,
    addHospitalName = {
        Cancel: "Cancel",
        AutogeneratePassword: "Auto Generate Password",
        CreateaPassword: "Create a Password",
        Save: "Save",
    },
}) => {
    const [passwordType, setPasswordType] = useState<"generate_password" | "create_password" | "">("");
    const [openAutoPopup, setOpenAutoPopup] = useState(false);
    const [generatedPassword, setGeneratedPassword] = useState<string>("");
    const [showPassword, setShowPassword] = useState(false);

    const isRequired =
        pathAction === "add" ||
        (hospitalDetailsData?.hospitalAdminInfo?.phone !==
            watch("hospitalAdminManagementMobileNumber") &&
            hospitalDetailsData?.hospitalAdminInfo?.email !==
            watch("hospitalAdminManagementEmail"));

    // ✅ Clear clipboard safely
    const clearClipboard = async () => {
        try {
            await navigator.clipboard.writeText("");
        } catch {
            console.warn("Clipboard clear not supported in this environment.");
        }
    };

    const handleCopyPassword = () => {
        if (generatedPassword) {
            navigator.clipboard.writeText(generatedPassword);
            successToast("Password copied to clipboard!");
        }
    };

    const handlePasswordModeChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value as "generate_password" | "create_password";
        setPasswordType(value);
        await clearClipboard();

        if (value === "generate_password") {
            // 🔹 Clear any previously cached password before opening popup
            setGeneratedPassword("");
            setValue(name, "");
            await clearClipboard();
            setOpenAutoPopup(true);
        } else {
            setGeneratedPassword("");
            setValue(name, "");
            await clearClipboard();
        }
    };

    const handleCloseAutoPopup = async () => {
        setOpenAutoPopup(false);
        setPasswordType("");
        setGeneratedPassword("");
        setValue(name, "");
        await clearClipboard();
    };


    return (
        <Box className="pb-4">
            {/* 🔹 Radio Buttons */}
            <div className="flex flex-col ">
                <div className="flex items-center gap-3 my-3">
                    <input
                        type="radio"
                        value="generate_password"
                        id="generate_password"
                        name="password_mode"
                        checked={passwordType === "generate_password"}
                        onChange={handlePasswordModeChange}
                        disabled={disabled}
                    />
                    <label
                        htmlFor="generate_password"
                        className={`text-14 font-semibold ${errors?.type_of_password
                            ? "text-error"
                            : passwordType === "generate_password"
                                ? "text-primary"
                                : "text-secondary"
                            }`}
                    >
                        {addHospitalName.AutogeneratePassword}
                    </label>
                </div>

                <div className="flex items-center gap-3 my-3">
                    <input
                        type="radio"
                        value="create_password"
                        id="create_password"
                        name="password_mode"
                        checked={passwordType === "create_password"}
                        onChange={handlePasswordModeChange}
                        disabled={disabled}
                    />
                    <label
                        htmlFor="create_password"
                        className={`text-14 font-semibold ${errors?.type_of_password
                            ? "text-error"
                            : passwordType === "create_password"
                                ? "text-primary"
                                : "text-secondary"
                            }`}
                    >
                        {addHospitalName.CreateaPassword}
                    </label>
                </div>
            </div>

            {/* 🔹 Shared Password Input (used for both modes) */}
            {
                (passwordType === "create_password" || generatedPassword) && (
                    <div className="mt-3 flex flex-col gap-3">
                        <TextField
                            type={showPassword ? "text" : "password"}
                            label="Password"
                            value={generatedPassword}
                            fullWidth
                            onChange={(e) => {
                                const val = e.target.value;
                                setGeneratedPassword(val);
                                setValue(name, val);
                                if (val) setPasswordType("create_password");
                            }}
                            error={Boolean(errors?.[name])}
                            helperText={errors?.[name]?.message ?? ""}
                            required={isRequired}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {generatedPassword && (
                            <div className="flex items-center gap-2">
                                <Tooltip title="Copy Password">
                                    <IconButton onClick={handleCopyPassword} color="primary">
                                        <CopyAll />
                                    </IconButton>
                                </Tooltip>

                                {/* Mask the password visually */}
                                <span className="font-medium text-md tracking-widest select-none">
                                    {"•".repeat(generatedPassword.length)} {/* or "*" */}
                                </span>
                            </div>
                        )}


                        {passwordType === "create_password" && (
                            <div>
                                {/* <Button
                                    variant="outlined"

                                >
                                    Cancel
                                </Button> */}
                                <SecondaryButton
                                    padding="py-2 px-5 hover:shadow-xs"
                                    type="button"
                                    name={addHospitalName.Cancel}
                                    onClick={async () => {
                                        setPasswordType("");
                                        setGeneratedPassword("");
                                        setValue(name, "");
                                        await clearClipboard();
                                    }} />
                            </div>

                        )}
                    </div>
                )
            }

            {/* 🔹 Auto Generate Popup */}
            <Dialog open={openAutoPopup} onClose={handleCloseAutoPopup} fullWidth maxWidth="sm">
                <DialogTitle className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-primary">Auto Generate Password</span>
                    <IconButton onClick={handleCloseAutoPopup}>
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <AutoGeneratePassword
                        name={name}
                        control={control}
                        setValue={(key: string, val: string) => {
                            setValue(key, val);
                            if (key === name) {
                                setGeneratedPassword(val);
                                setPasswordType("generate_password");
                            }
                        }}
                        clearErrors={clearErrors}
                        errors={errors}
                        required={isRequired}
                        createNew={true}
                    />
                </DialogContent>

                <DialogActions>

                    <SecondaryButton
                        padding="lg:px-18 py-3 px-5"
                        type="button"
                        name={addHospitalName.Cancel}
                        onClick={handleCloseAutoPopup}
                    />
                    <button
                        className={`bg-blue-300 whitespace-nowrap hover:shadow-xl text-white text-16 font-semibold py-4 px-6 rounded-full flex items-center gap-2 lg:px-18 py-3 px-5`}

                        color="primary"
                        type="submit"
                        disabled={!generatedPassword} // ✅ Prevent save when no password generated
                        onClick={async () => {
                            if (!generatedPassword) return; // ✅ Double safety
                            successToast("Password saved successfully!");
                            setValue(name, generatedPassword);
                            await clearClipboard();
                            setOpenAutoPopup(false);
                            setPasswordType("generate_password");
                        }}
                    >
                        Save
                    </button>

                </DialogActions>
            </Dialog>
        </Box >
    );
};

export default PasswordSection;
